import SoftphoneDriver, { requestMicrophone } from "./SoftphoneDriver";

/**
 * ExotelDriver — wraps Exotel's Voice WebSDK behind the SoftphoneDriver contract.
 *
 * ⚠️ **This is the only file in the app that may reference the vendor SDK.**
 * Everything else — the hook, the widget, the fallback path — talks to
 * SoftphoneDriver, which is what let all of it ship before the SDK existed.
 *
 * ── Why the SDK is loaded from a global, not imported ──
 * The Exotel Voice WebSDK **is not on public npm**. Their README says "contact us
 * to get the credentials to download the node.js package", and the integration
 * guide is a PDF behind an Exotel account. A static `import` of a package that
 * isn't in package.json fails the *build*, taking the whole CRM down for a
 * feature most institutes don't use — so the SDK is picked up at runtime from
 * `window` (which is how a vendor bundle delivered as a script tag arrives) and
 * its absence degrades to phone mode instead.
 *
 * ── When the credentials arrive ──
 *  1. Load the vendor bundle (script tag in `public/index.html`, or add the
 *     private package and import it *here only*).
 *  2. Confirm the global name below matches what it attaches.
 *  3. Map the SDK's event names in `_bindSdkEvents` — the shape below is written
 *     against their documented event vocabulary but is **unverified against a
 *     live account**, exactly like the IP-PSTN webhook parser on the backend.
 * Nothing outside this file changes.
 *
 * ── Also still unanswered by Exotel (P7.0) ──
 * whether an agent's SIP identity can be created per agent via API or only by
 * their staff, and whether IP-PSTN is limited to certain KYC regions. Neither
 * blocks this file; both decide whether onboarding is self-serve.
 */

/** Names an Exotel browser bundle is known or likely to attach itself to. */
const SDK_GLOBALS = ["ExotelWebSDK", "ExotelSDK", "Exotel"];

function findSdk() {
  for (const key of SDK_GLOBALS) {
    if (typeof window !== "undefined" && window[key]) return window[key];
  }
  return null;
}

export default class ExotelDriver extends SoftphoneDriver {
  constructor() {
    super();
    this._sdk = null;
    this._session = null;
    this._call = null;
    this._stream = null;
  }

  get name() { return "exotel"; }

  async isAvailable() {
    if (!findSdk()) {
      // Deliberately specific. "Browser calling unavailable" would send an admin
      // hunting through their own settings for something that is not there.
      return {
        ok: false,
        reason: "The Exotel calling library isn't loaded on this deployment. "
          + "Browser calling has to be enabled by Exotel for your account first — "
          + "until then, calls will ring your phone.",
      };
    }
    if (!window.isSecureContext) {
      return { ok: false, reason: "Browser calling needs a secure (https) connection." };
    }
    return { ok: true };
  }

  async register({ sipUri, sipIdentity, sipDomain, displayName, onEvent } = {}) {
    this._onEvent = onEvent || (() => {});
    this._emit({ type: "registering" });

    const available = await this.isAvailable();
    if (!available.ok) {
      this._emit({ type: "registration_failed", message: available.reason });
      return { ok: false, reason: available.reason };
    }

    // Ask for the mic BEFORE registering. Registering first would advertise this
    // browser as reachable and then drop every call that arrived — worse than
    // never registering, because the caller hears ringing.
    const mic = await requestMicrophone();
    if (!mic.ok) {
      this._emit({ type: "registration_failed", message: mic.reason });
      return { ok: false, reason: mic.reason };
    }
    this._stream = mic.stream;

    try {
      this._sdk = findSdk();
      // ⚠️ Vendor surface, unverified — see the file header. Kept to one call so
      // there is exactly one line to correct against the real SDK.
      this._session = await this._sdk.init({
        userId: sipIdentity,
        domain: sipDomain,
        sipUri,
        displayName,
      });
      this._bindSdkEvents();
      this._emit({ type: "registered" });
      return { ok: true };
    } catch (err) {
      const message = err?.message || "Could not connect to Exotel.";
      this._stream?.getTracks?.().forEach((t) => t.stop());
      this._stream = null;
      this._emit({ type: "registration_failed", message });
      return { ok: false, reason: message };
    }
  }

  /**
   * Translate the SDK's events into ours.
   *
   * The listener names below follow Exotel's documented IP-PSTN vocabulary — the
   * same event set the backend's webhook parser handles — so the two halves stay
   * describable in one language even though they arrive by different routes.
   */
  _bindSdkEvents() {
    const on = (event, handler) => {
      this._session?.on?.(event, handler);
    };

    on("incoming_call", (payload) => {
      this._call = {
        id: payload?.callSid || payload?.CallSid || `exotel-${Date.now()}`,
        from: payload?.from || payload?.From || "",
        direction: "inbound",
        muted: false,
        held: false,
      };
      this._emit({ type: "incoming", call: this._call });
    });

    on("call_ringing", (payload) => {
      this._call = this._call || {
        id: payload?.callSid || `exotel-${Date.now()}`,
        to: payload?.to || payload?.To || "",
        direction: "outbound",
        muted: false,
        held: false,
      };
      this._emit({ type: "ringing", call: this._call });
    });

    on("call_answered", () => {
      if (!this._call) return;
      this._call = { ...this._call, answeredAt: Date.now() };
      this._emit({ type: "answered", call: this._call });
    });

    const ended = (reason) => () => {
      const call = this._call;
      this._call = null;
      this._emit({ type: "ended", call, reason });
    };
    on("call_completed", ended("remote"));
    on("call_missed", ended("missed"));

    on("error", (err) => {
      this._emit({ type: "error", message: err?.message || "Calling error." });
    });

    // A registration that drops silently means calls ring nowhere, and the
    // counsellor has no way to know. Surfacing it is what makes the fallback to
    // phone mode possible at all.
    on("unregistered", () => {
      this._emit({ type: "unregistered" });
    });
  }

  async unregister() {
    try {
      await this._session?.disconnect?.();
    } catch {
      /* disconnecting a session that is already gone is not an error */
    }
    // Always release the mic, even if the SDK teardown failed — a live track
    // keeps the browser's recording indicator on, which reads as being listened to.
    this._stream?.getTracks?.().forEach((t) => t.stop());
    this._stream = null;
    this._session = null;
    this._call = null;
    this._emit({ type: "unregistered" });
  }

  async answer() {
    try {
      await this._session?.answer?.();
    } catch (err) {
      this._emit({ type: "error", message: err?.message || "Could not answer." });
    }
  }

  async hangup() {
    try {
      await this._session?.hangup?.();
    } catch (err) {
      this._emit({ type: "error", message: err?.message || "Could not hang up." });
    }
    // Emit locally too: if the SDK never confirms, the UI must still let go of
    // the call rather than sit on a dead one forever.
    if (this._call) {
      const call = this._call;
      this._call = null;
      this._emit({ type: "ended", call, reason: "local" });
    }
  }

  async mute(on) {
    try {
      await this._session?.mute?.(!!on);
      if (this._call) this._call = { ...this._call, muted: !!on };
    } catch (err) {
      this._emit({ type: "error", message: err?.message || "Could not mute." });
    }
  }

  async hold(on) {
    try {
      await this._session?.hold?.(!!on);
      if (this._call) this._call = { ...this._call, held: !!on };
    } catch (err) {
      this._emit({ type: "error", message: err?.message || "Could not hold." });
    }
  }

  async sendDtmf(digit) {
    try {
      await this._session?.sendDtmf?.(String(digit));
    } catch (err) {
      this._emit({ type: "error", message: err?.message || "Could not send tone." });
    }
  }
}
